import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { FaUserMd, FaPlus, FaPen, FaTrash, FaTimes, FaSync } from 'react-icons/fa';
import './Doctors.css';

const EMPTY = { name: '', speciality: '', description: '', email: '', work_start: '09:00', work_end: '17:00', slot_minutes: 30, active: true };

const Doctors = () => {
  const { t } = useTranslation();
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const token = localStorage.getItem('access_token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchDoctors = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/healthcare/doctors', { headers });
      setDoctors(res.data?.doctors || []);
    } catch (err) {
      console.error('Failed to load doctors:', err);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { fetchDoctors(); }, [fetchDoctors]);

  const openCreate = () => { setEditingId(null); setForm(EMPTY); setError(''); setModalOpen(true); };
  const openEdit = (d) => {
    setEditingId(d.id);
    setForm({ name: d.name || '', speciality: d.speciality || '', description: d.description || '', email: d.email || '', work_start: d.work_start || '09:00', work_end: d.work_end || '17:00', slot_minutes: d.slot_minutes || 30, active: d.active !== false });
    setError('');
    setModalOpen(true);
  };

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) { setError(t('doctors.nameRequired', 'Name is required.')); return; }
    setSaving(true);
    setError('');
    try {
      const payload = { ...form, slot_minutes: Number(form.slot_minutes) };
      if (editingId) await axios.put(`/api/healthcare/doctors/${editingId}`, payload, { headers });
      else await axios.post('/api/healthcare/doctors', payload, { headers });
      setModalOpen(false);
      fetchDoctors();
    } catch (err) {
      setError(err?.response?.data?.detail || t('doctors.saveError', 'Failed to save doctor.'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t('doctors.confirmDelete', 'Delete this doctor?'))) return;
    try {
      await axios.delete(`/api/healthcare/doctors/${id}`, { headers });
      fetchDoctors();
    } catch (err) {
      console.error('Failed to delete doctor:', err);
    }
  };

  return (
    <div className="dr-page">
      <header className="dr-header">
        <div className="dr-header-title">
          <FaUserMd className="dr-header-icon" />
          <div>
            <h1>{t('doctors.title', 'Doctors & Surgeons')}</h1>
            <p>{t('doctors.subtitle', 'Manage practitioners; the AI receptionist uses this roster to book appointments.')}</p>
          </div>
        </div>
        <div className="dr-header-actions">
          <button type="button" className="dr-btn ghost" onClick={fetchDoctors} disabled={loading}><FaSync /> {t('doctors.refresh', 'Refresh')}</button>
          <button type="button" className="dr-btn primary" onClick={openCreate}><FaPlus /> {t('doctors.add', 'Add doctor')}</button>
        </div>
      </header>

      {loading ? (
        <div className="dr-state">{t('doctors.loading', 'Loading...')}</div>
      ) : doctors.length === 0 ? (
        <div className="dr-state">{t('doctors.empty', 'No doctors yet. Add one to get started.')}</div>
      ) : (
        <div className="dr-grid">
          {doctors.map((d) => (
            <div key={d.id} className={`dr-card${d.active === false ? ' inactive' : ''}`}>
              <div className="dr-card-head">
                <div className="dr-avatar"><FaUserMd /></div>
                <div className="dr-actions">
                  <button type="button" onClick={() => openEdit(d)} title={t('doctors.edit', 'Edit')}><FaPen /></button>
                  <button type="button" onClick={() => remove(d.id)} title={t('doctors.delete', 'Delete')}><FaTrash /></button>
                </div>
              </div>
              <h3>Dr {d.name}</h3>
              {d.speciality && <span className="dr-spec">{d.speciality}</span>}
              {d.description && <p className="dr-desc">{d.description}</p>}
              <div className="dr-hours">{d.work_start} – {d.work_end} · {d.slot_minutes} min</div>
            </div>
          ))}
        </div>
      )}

      {modalOpen && (
        <div className="dr-modal" onClick={() => setModalOpen(false)}>
          <div className="dr-modal-content" onClick={(e) => e.stopPropagation()}>
            <header className="dr-modal-head">
              <h3>{editingId ? t('doctors.editTitle', 'Edit doctor') : t('doctors.addTitle', 'Add doctor')}</h3>
              <button type="button" onClick={() => setModalOpen(false)}><FaTimes /></button>
            </header>
            {error && <div className="dr-alert">{error}</div>}
            <div className="dr-field"><label>{t('doctors.name', 'Name')}</label><input value={form.name} onChange={(e) => setField('name', e.target.value)} /></div>
            <div className="dr-field"><label>{t('doctors.speciality', 'Speciality')}</label><input value={form.speciality} onChange={(e) => setField('speciality', e.target.value)} placeholder="Cardiologue, Chirurgien..." /></div>
            <div className="dr-field"><label>{t('doctors.description', 'Description')}</label><textarea rows="2" value={form.description} onChange={(e) => setField('description', e.target.value)} placeholder={t('doctors.descPlaceholder', 'Used by the AI agent to route patients to the right practitioner.')} /></div>
            <div className="dr-field"><label>{t('doctors.email', 'Email (optional)')}</label><input type="email" value={form.email} onChange={(e) => setField('email', e.target.value)} /></div>
            <div className="dr-row">
              <div className="dr-field"><label>{t('doctors.workStart', 'Start')}</label><input type="time" value={form.work_start} onChange={(e) => setField('work_start', e.target.value)} /></div>
              <div className="dr-field"><label>{t('doctors.workEnd', 'End')}</label><input type="time" value={form.work_end} onChange={(e) => setField('work_end', e.target.value)} /></div>
              <div className="dr-field"><label>{t('doctors.slot', 'Slot (min)')}</label><input type="number" min="5" max="240" step="5" value={form.slot_minutes} onChange={(e) => setField('slot_minutes', e.target.value)} /></div>
            </div>
            <div className="dr-field dr-check">
              <label><input type="checkbox" checked={form.active} onChange={(e) => setField('active', e.target.checked)} /> {t('doctors.active', 'Active (bookable)')}</label>
            </div>
            <div className="dr-modal-actions">
              <button type="button" className="dr-btn ghost" onClick={() => setModalOpen(false)}>{t('doctors.cancel', 'Cancel')}</button>
              <button type="button" className="dr-btn primary" onClick={save} disabled={saving}>{saving ? t('doctors.saving', 'Saving...') : t('doctors.save', 'Save')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Doctors;
